import { useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import { adminProviders } from "~/services";
import { errorToast, Modal } from "~/ui";
import { validateError } from "~/utils";
import { EditProviderScheduleFormFields, ErrorAlert } from ".";

export const EditProviderDetailsModal = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => {
  const [rowsErrors, setRowsErrors] = useState<Record<string, string[]> | null>(
    null,
  );
  const params = useParams<{ providerId: string }>();

  const uploadScheduleMutation =
    adminProviders.useUploadProviderScheduleMutation({
      onError: (apiError: unknown) => {
        const parsedError = validateError(apiError);
        if (!parsedError) {
          errorToast();
          return;
        }
        const { code, message, fields } = parsedError.response.data.error;
        if (
          code === "validation_failed" &&
          fields &&
          Object.keys(fields).length > 0
        ) {
          setRowsErrors(fields);
        } else {
          errorToast(message);
        }
      },
    });

  const { data: scheduleMetaResponse } =
    adminProviders.useGetProviderScheduleMeta(params.providerId ?? "");

  // TODO: if there is a previous schedule, load initial values
  const { handleSubmit, control, watch } =
    useForm<adminProviders.UploadProviderScheduleParams>({
      defaultValues: {
        description: "",
        providerId: params.providerId,
      },
    });

  const file = watch("file");

  const onSubmit = (data: adminProviders.UploadProviderScheduleParams) => {
    if (!params.providerId) return;

    uploadScheduleMutation.mutate(
      {
        providerId: params.providerId,
        file: data.file,
        description: data.description,
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      className="gap-6"
      show={show}
      title="Edit clinician schedule"
      onClose={onClose}
      goBack={onClose}
      handleConfirm={handleSubmit(onSubmit)}
      confirmLabel="Save"
      closeLabel="Cancel"
      disableConfirm={!file || uploadScheduleMutation.isPending}
    >
      <div className="flex h-full flex-col justify-between">
        <div>
          <h2 className="text-indigo pb-4 text-xl font-medium">
            {scheduleMetaResponse?.data?.providerName}
          </h2>

          <EditProviderScheduleFormFields control={control} />
        </div>
        {rowsErrors && (
          <ErrorAlert
            error={rowsErrors}
            onDismiss={() => setRowsErrors(null)}
          />
        )}
      </div>
    </Modal>
  );
};
